import SunCalc from 'suncalc';
import Store from './Store';

/**
 * Premium frontend behaviour.
 *
 * These implementations previously shipped inside the free plugin's app.min.js,
 * gated by an `isPremium` check. WordPress.org guideline 5 does not permit
 * shipping locked feature code in the free plugin, so they live here and are
 * attached to the free plugin's live objects at runtime.
 *
 * Every method body below is copied verbatim from WP Dark Mode 5.3.11 so that
 * behaviour is identical to the previous release.
 */
const Premium = {

	/**
	 * Attaches all premium behaviour to the free plugin's objects.
	 */
	init() {
		// Bail if the free plugin is not present or its frontend is disabled.
		if (typeof wp_dark_mode_json === 'undefined') {
			return;
		}

		this.extendApp();
		this.extendVideos();
		this.extendColor();
		this.extendAccessibility();
	},

	/**
	 * Time and sunset modes, timezone and location storage.
	 */
	extendApp() {
		const app = window.WPDarkModeApp;

		if (!app) {
			return;
		}

		app.applyTimeMode = function () {
			const starts = wp_dark_mode_json.options.frontend_time_starts;
			const ends = wp_dark_mode_json.options.frontend_time_ends;

			// Helper function to convert "hh:mm AA" format to minutes since midnight
			function timeToMinutes(time) {
				const [timePart, period] = time.split(' ');
				let [hours, minutes] = timePart.split(':').map(Number);

				// Convert to 24-hour format
				if (period === 'PM' && hours !== 12) {
					hours += 12;
				} else if (period === 'AM' && hours === 12) {
					hours = 0;
				}

				return hours * 60 + minutes;
			}

			// Get the current time in minutes since midnight
			const now = new Date();
			const currentMinutes = now.getHours() * 60 + now.getMinutes();

			const startMinutes = timeToMinutes(starts);
			const endMinutes = timeToMinutes(ends);

			let isDarkMode = false;

			// Check if current time is between start and end time
			if (startMinutes < endMinutes) {
				// Time range is within the same day
				if (currentMinutes >= startMinutes && currentMinutes < endMinutes) {
					isDarkMode = true;
				}
			} else {
				// Time range spans across midnight
				if (currentMinutes >= startMinutes || currentMinutes < endMinutes) {
					isDarkMode = true;
				}
			}

			if (isDarkMode) {
				WPDarkMode.activate();
			} else {
				WPDarkMode.deactivate();
			}
		};

		app.applySunsetMode = async function () {
			// Get sunset time.
			const loc = Store.get('location').split(',').map(Number);
			let { sunrise, sunset } = SunCalc.getTimes(new Date(), loc[0], loc[1]);

			// Get current time.
			const now = new Date();

			// Check if it is night time.
			const isNight = now > sunset || now < sunrise;

			// Apply dark mode, if it is night time.
			if (isNight) {
				WPDarkMode.activate();
			} else {
				WPDarkMode.deactivate();
			}
		};

		app.syncDeviceLocation = async function () {
			// Check if location was updated in last 24 hours.
			const locationUpdated = Store.get('location-updated');

			// Bail if location was updated in last 24 hours.
			if (locationUpdated) {
				if (((new Date()) - (new Date().setTime(locationUpdated))) < (24 * 60 * 60 * 1000)) {
					return;
				}
			}

			// Bail, if navigator is not available or geolocation is not supported.
			if (!navigator || !navigator.geolocation) {
				return;
			}

			navigator.geolocation.getCurrentPosition((position) => {
				// Save location for dark mode.
				Store.set('location', position.coords.latitude + ',' + position.coords.longitude);

				// Save location update time.
				Store.set('location-updated', new Date().getTime());

				// Apply sunset mode if it is enabled.
				if (wp_dark_mode_json.options.frontend_mode === 'sunset') {
					this.applySunsetMode();
				}
			});
		};

		app.syncPremiumStorage = function () {
			/**
			 * Timezone of device, used for Time based dark mode
			 */
			let timeZoneOffset = (new Date().getTimezoneOffset() / 60);

			// Add + sign if offset is negative. Add - sign if offset is positive.
			if (timeZoneOffset > 0) {
				timeZoneOffset = '-' + timeZoneOffset;
			} else {
				timeZoneOffset = '+' + Math.abs(Number(timeZoneOffset));
			}

			Store.set('timezone', timeZoneOffset);

			/**
			 * Location of device, used for sunset based dark mode
			 * Ask, only if sunset mode is enabled.
			 */
			if (wp_dark_mode_json.options.frontend_mode === 'sunset') {
				this.syncDeviceLocation();
			}
		};

		/**
		 * syncStorage() and tryApplyingDarkMode() already ran during the free
		 * plugin's synchronous init, before this bundle executed. Re-run the
		 * premium parts now that the real implementations are attached.
		 *
		 * tryApplyingDarkMode() is deliberately not re-invoked wholesale: it
		 * dispatches the `wp_dark_mode` event, and firing that twice would make
		 * every listener react again. Only the premium mode branches are re-run.
		 */
		app.syncPremiumStorage();

		const mode = wp_dark_mode_json.options.frontend_mode;

		if ('time' === mode) {
			app.applyTimeMode();
		} else if ('sunset' === mode && Store.get('location')) {
			app.applySunsetMode();
		}
	},

	/**
	 * Dark/light video source replacement.
	 */
	extendVideos() {
		const videos = window.WPDarkModeVideos;

		if (!videos) {
			return;
		}

		// Normalise saved YouTube/Vimeo URLs to their embeddable form. This ran
		// in free's Videos.js::initVideos() before; the light version no longer
		// touches video_replaces, so it runs here.
		if (Array.isArray(wp_dark_mode_json.options.video_replaces)) {
			wp_dark_mode_json.options.video_replaces = wp_dark_mode_json.options.video_replaces.map((vid) => {
				['light', 'dark'].forEach((mode) => {
					const host = videos.getVideoHost(vid[mode]);

					if ('youtube' === host) {
						const videoID = videos.getYouTubeVideoID(vid[mode]);
						if (videoID) vid[mode] = `https://www.youtube.com/embed/${videoID}`;
					}

					if ('vimeo' === host) {
						const videoID = videos.getVimeoVideoID(vid[mode]);
						if (videoID) vid[mode] = `https://player.vimeo.com/video/${videoID}`;
					}
				});

				return vid;
			});
		}

		videos.getDarkSrc = function (src = null) {
			// Bail, if src is not set.
			if (!src) return;

			const replaceable_videos = wp_dark_mode_json.options.video_replaces || false;
			if (!replaceable_videos) return false

			const videoPair = replaceable_videos.find((vid) => {
				// remove query string from video src
				const srcWithoutQuery = src.split('?')[0];

				return vid.light === srcWithoutQuery
			});

			if (!videoPair) return false

			return videoPair.dark;
		};

		videos.replaceVideos = function () {
			const videoEls = document.querySelectorAll('video, iframe, video source');

			// Bail, if videos are not set.
			if (!videoEls || !videoEls.length) return;

			// Replace videos.
			videoEls.forEach((video) => {
				// Bail, if video src is not set.
				if (!video.src) return;

				let currentSrc = video.dataset.lightSrc || video.src;

				// Add wp-dark-mode-ignore class.
				video.classList.add('wp-dark-mode-ignore');

				if (WPDarkMode.isActive) {
					const darkSrc = this.getDarkSrc(currentSrc);
					if (darkSrc) {
						video.dataset.lightSrc = currentSrc;
						video.dataset.darkSrc = darkSrc;
						video.src = darkSrc;
						video.srcset = darkSrc;
					}
				} else {
					const lightSrc = video.dataset.lightSrc;
					if (lightSrc) {
						video.src = lightSrc;
						video.srcset = lightSrc;
					}
				}
			});
		};

		/**
		 * Exclude low brightness/grayscale videos.
		 *
		 * Previously shipped inside free's Videos.js::setVideoFilters(),
		 * gated by video_enabled_low_brightness/grayscale being forced empty
		 * for free installs. Moved here so free's own code doesn't contain
		 * this logic at all.
		 */
		videos.setVideoFilters = function () {
			const lowBrightnessExcludes = wp_dark_mode_json.options.video_enabled_low_brightness ? wp_dark_mode_json.options.video_low_brightness_excludes : [];
			const lowGrayscaleExcludes = wp_dark_mode_json.options.video_enabled_low_grayscale ? wp_dark_mode_json.options.video_low_grayscale_excludes : [];

			const videoEls = document.querySelectorAll('video, iframe, video source');

			if (videoEls && videoEls.length) {
				videoEls.forEach((video) => {
					const src = video.src;

					if (lowBrightnessExcludes && lowBrightnessExcludes.includes(src)) {
						video?.style.setProperty('--wp-dark-video-brightness', '100%');
					}

					if (lowGrayscaleExcludes && lowGrayscaleExcludes.includes(src)) {
						video?.style.setProperty('--wp-dark-video-grayscale', '0');
					}
				});
			}
		};

		// The free plugin already ran initVideos() on DOMContentLoaded or will
		// shortly; re-run the replacement so the premium behaviour applies.
		videos.replaceVideos();
		videos.setVideoFilters();

		// Free's own toggle listener no longer re-runs setVideoFilters (that
		// logic lives here now), so re-attach on the same event free fires.
		// Premium.init() runs twice (immediate + DOMContentLoaded) by design,
		// so guard against registering this listener a second time.
		if (!this._videoFiltersListenerAttached) {
			this._videoFiltersListenerAttached = true;
			document.addEventListener('wp_dark_mode', () => {
				videos.setVideoFilters();
			});
		}
	},

	/**
	 * Include rules.
	 */
	extendColor() {
		const color = window.WPDarkModeColor;

		if (!color) {
			return;
		}

		color.handleIncludes = function () {
			const includedElements = document.querySelectorAll(wp_dark_mode_json.options.excludes_elements_includes || '.wp_dark_mode_include');

			if (!includedElements || !includedElements.length) return;

			includedElements.forEach(element => {
				if (!element || !(element instanceof HTMLElement)) return;

				element.classList.add('wp-dark-mode-include');
				element.classList.remove('wp-dark-mode-ignore');
			});
		};

		// removeLoader() already ran during the free plugin's init.
		color.handleIncludes();
	},

	/**
	 * Large font / typography scaling.
	 */
	extendAccessibility() {
		const accessibility = window.WPDarkModeAccessibility;

		if (!accessibility) {
			return;
		}

		accessibility.calculateFontSize = (size, by = 1) => {
			const unit = size.replace(/[0-9]/g, '');
			const value = parseFloat(size.replace(/[a-z]/g, ''));

			let newValue = 0;
			newValue = value * by;
			return `${newValue}${unit}`;
		};

		accessibility.initLargeFont = () => {
			if (!wp_dark_mode_json.options.typography_enabled) return;

			const elements = document.querySelectorAll('*:not(.wp-dark-mode-ignore):not(.wp-dark-mode-switch):not(script):not(style):not(link):not(meta):not(title):not(base):not(head):not(.wp-dark-mode-switch)');

			let desiredSize = wp_dark_mode_json.options.typography_font_size;
			if (desiredSize === 'custom') {
				desiredSize = wp_dark_mode_json.options.typography_font_size_custom / 100;
			}

			elements.forEach(element => {
				// Bail, if any of parent has wp-dark-mode-ignore class.
				if (element.closest('.wp-dark-mode-ignore')) {
					return;
				}

				const fontSize = getComputedStyle(element).fontSize;

				if (fontSize) {
					// Calculate new font size
					const newFontSize = accessibility.calculateFontSize(fontSize, desiredSize);

					// Set property
					element.style.setProperty('--wp-dark-large-font-size', newFontSize);
				}
			});
		};

		accessibility.toggleFontSize = function (element) {
			if (!wp_dark_mode_json.options.typography_enabled) return;

			const html = document.querySelector('html');

			// Is large font is active
			const isLargeFont = html.classList.contains('wp-dark-mode-large-font');

			// Toggle large font
			html.classList[isLargeFont ? 'remove' : 'add']('wp-dark-mode-large-font');

			// Is element is
			const isActive = element.classList.contains('active');
			const newIsActive = !isActive;

			// Toggle active class
			element.classList[isActive ? 'remove' : 'add']('active');

			// Use plain button role with NO state attributes to prevent announcements
			element.setAttribute('role', 'button');
			element.removeAttribute('aria-checked');
			element.removeAttribute('aria-pressed');

			// Only set aria-label once on initialization, don't change it
			if (!element.getAttribute('aria-label')) {
				element.setAttribute('aria-label', 'Toggle Font Size');
			}

			// Announce via aria-live region
			const stateLabel = newIsActive ? 'Large Font On' : 'Normal Font On';
			const announcer = document.getElementById('wp-dark-mode-announcer');
			if (announcer) {
				announcer.textContent = '';
				setTimeout(() => {
					announcer.textContent = stateLabel;
				}, 50);
			}

			// Remember user choice
			Store.set('large-font', !isLargeFont ? 'on' : 'off');

			// Reactive other switches
			const switches = document.querySelectorAll('.wp-dark-mode-switch ._font');

			if (!switches || !switches.length) return;

			switches.forEach(switchEl => {
				switchEl.classList[isActive ? 'remove' : 'add']('active');
				switchEl.setAttribute('role', 'button');
				switchEl.removeAttribute('aria-checked');
				switchEl.removeAttribute('aria-pressed');

				// Only set aria-label once on initialization, don't change it
				if (!switchEl.getAttribute('aria-label')) {
					switchEl.setAttribute('aria-label', 'Toggle Font Size');
				}
			});
		};

		accessibility.checkLargeFont = () => {
			const isLargeFont = Store.get('large-font') === 'on';

			if (isLargeFont) {
				document.querySelector('html').classList.add('wp-dark-mode-large-font');

				// On wp-dark-mode-switches-updated event
				document.addEventListener('wp-dark-mode-switches-updated', () => {
					const switches = document.querySelectorAll('.wp-dark-mode-switch ._font');
					if (!switches || !switches.length) return;
					switches.forEach(switchEl => {
						switchEl.classList.add('active');
						switchEl.setAttribute('role', 'button');
						switchEl.removeAttribute('aria-checked');
						switchEl.removeAttribute('aria-pressed');

						// Only set aria-label once on initialization, don't change it
						if (!switchEl.getAttribute('aria-label')) {
							switchEl.setAttribute('aria-label', 'Toggle Font Size');
						}
					});
				});
			} else {
				// When large font is OFF, ensure switches reflect that
				document.addEventListener('wp-dark-mode-switches-updated', () => {
					const switches = document.querySelectorAll('.wp-dark-mode-switch ._font');
					if (!switches || !switches.length) return;
					switches.forEach(switchEl => {
						switchEl.classList.remove('active');
						switchEl.setAttribute('role', 'button');
						switchEl.removeAttribute('aria-checked');
						switchEl.removeAttribute('aria-pressed');

						// Only set aria-label once on initialization, don't change it
						if (!switchEl.getAttribute('aria-label')) {
							switchEl.setAttribute('aria-label', 'Toggle Font Size');
						}
					});
				});
			}
		};

		// ready() already ran in the free plugin with the stub implementations.
		accessibility.initLargeFont();
		accessibility.checkLargeFont();
	},
};

export default Premium;
